import { useEffect, useRef, useState } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import NoDataAnimation from '../animation/NoDataAnimation';
import { renderXRechnungHtml } from '../../utils/xrechnung';

/**
 * Renders an XRechnung / ZUGFeRD XML using the official KoSIT visualization
 * stylesheets inside a sandboxed iframe.
 *
 * @param {string} xml   raw e-invoice XML
 */
const XRechnungPreview = ({ xml, className = '' }) => {
  const { t, i18n } = useTranslation();

  const [html, setHtml] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const requestId = useRef(0);

  const lang = (i18n.language || 'de').startsWith('en') ? 'en' : 'de';

  useEffect(() => {
    if (!xml) {
      setHtml(null);
      setError(null);
      return;
    }
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    renderXRechnungHtml(xml, { lang })
      .then((result) => {
        if (id !== requestId.current) return;
        setHtml(result);
      })
      .catch((err) => {
        if (id !== requestId.current) return;
        console.error('XRechnung visualization failed', err);
        setError(
          err?.message === 'UNSUPPORTED_XML'
            ? t('This XML is not a supported e-invoice (XRechnung / ZUGFeRD)')
            : t('The e-invoice could not be visualized'),
        );
      })
      .finally(() => {
        if (id === requestId.current) setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [xml, lang]);

  if (error) {
    return (
      <Box
        className={`w-full h-full flex flex-col justify-center items-center ${className}`}
      >
        <NoDataAnimation size={90} />
        <Typography textAlign="center">{error}</Typography>
      </Box>
    );
  }

  if (loading || !html) {
    return (
      <Skeleton
        className={className}
        sx={{
          padding: 0,
          margin: 0,
          height: '100%',
          width: '100%',
          transform: 'unset',
          borderRadius: '0px',
        }}
      />
    );
  }

  return (
    <iframe
      title="XRechnung"
      className={`w-full h-full border-0 rounded-lg ${className}`}
      sandbox="allow-scripts allow-downloads allow-popups"
      srcDoc={html}
      style={{ backgroundColor: '#fff' }}
    />
  );
};

export default XRechnungPreview;
